import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { api } from "../api";
import { formatUnixInTimeZone } from "../datetime";
import { useDisplayTimeZone } from "../displayTimezone";

type Seg = { start: number; end: number; text: string };

type Recording = {
  id: string;
  title: string | null;
  r2_key: string | null;
  duration_secs: number | null;
  status: string;
  transcript: string | null;
  transcript_segments: string | null;
  error_message: string | null;
  created_at: number;
};

export default function Entry() {
  const displayTz = useDisplayTimeZone();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const t0 = parseFloat(searchParams.get("t") || "0") || 0;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [rec, setRec] = useState<Recording | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeLine, setActiveLine] = useState(-1);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const loadRecording = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const r = await api<{ recording: Recording }>(
      `/api/recordings/${encodeURIComponent(id)}`,
    );
    if (r.ok && r.data?.recording) setRec(r.data.recording);
    else setRec(null);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void loadRecording();
  }, [loadRecording]);

  const segments: Seg[] = useMemo(() => {
    if (!rec?.transcript_segments) return [];
    try {
      return JSON.parse(rec.transcript_segments) as Seg[];
    } catch {
      return [];
    }
  }, [rec?.transcript_segments]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !t0) return;
    const onMeta = () => {
      v.currentTime = t0;
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [t0, rec?.r2_key]);

  /** Safari (esp. iOS WebKit) expects inline + legacy webkit attribute for reliable playback. */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !rec?.r2_key) return;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
  }, [rec?.r2_key]);

  function seekTo(sec: number, idx: number) {
    const v = videoRef.current;
    if (v) {
      v.currentTime = sec;
      // Must run in same gesture as click for Safari; .catch handles autoplay policy denials.
      void v.play().catch(() => {});
    }
    setActiveLine(idx);
  }

  async function confirmDelete() {
    if (!rec) return;
    setDeleteBusy(true);
    setDeleteError(null);
    const r = await api<{ ok?: boolean }>(
      `/api/recordings/${encodeURIComponent(rec.id)}`,
      { method: "DELETE" },
    );
    setDeleteBusy(false);
    if (!r.ok) {
      setDeleteError(r.error || "Could not delete");
      return;
    }
    navigate("/library");
  }

  async function retryTranscription() {
    if (!rec) return;
    setRetryBusy(true);
    setRetryError(null);
    const r = await api<{ ok?: boolean; status?: string }>(
      `/api/recordings/${encodeURIComponent(rec.id)}/retry-transcription`,
      { method: "POST" },
    );
    setRetryBusy(false);
    if (!r.ok) {
      setRetryError(r.error || "Could not retry transcription");
      return;
    }
    setRec({
      ...rec,
      status: "processing_transcribe",
      error_message: null,
      transcript: null,
      transcript_segments: null,
    });
    setDeleteConfirm(false);
  }

  if (loading) {
    return (
      <>
        <Link to="/library">← Library</Link>
        <h1>Entry</h1>
        <p className="muted">Loading…</p>
      </>
    );
  }

  if (!rec) {
    return (
      <>
        <Link to="/library">← Library</Link>
        <h1>Not found</h1>
      </>
    );
  }

  const streamUrl = rec.r2_key
    ? `/api/media/${encodeURIComponent(rec.id)}/stream`
    : null;

  const canRetryTranscription = rec.status === "failed" && Boolean(rec.r2_key?.trim());

  return (
    <>
      <Link to="/library">← Library</Link>
      <div
        className="entry-page-head"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
          marginTop: "0.5rem",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 12rem" }}>
          <h1 style={{ marginTop: 0 }}>
            {rec.title || formatUnixInTimeZone(rec.created_at, displayTz)}
          </h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            Status: {rec.status}
          </p>
        </div>
        <div
          className="entry-toolbar-actions"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          {canRetryTranscription ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={retryBusy || deleteBusy}
              onClick={() => void retryTranscription()}
            >
              {retryBusy ? "Starting…" : "Retry transcription"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={deleteBusy || retryBusy}
            onClick={() => {
              setDeleteConfirm(true);
              setDeleteError(null);
            }}
          >
            Delete…
          </button>
        </div>
      </div>

      {rec.error_message ? <p className="error">{rec.error_message}</p> : null}
      {retryError ? <p className="error">{retryError}</p> : null}

      <ConfirmModal
        open={deleteConfirm}
        title="Delete entry"
        confirmLabel="Delete"
        busyConfirmLabel="Deleting…"
        confirmVariant="danger"
        busy={deleteBusy}
        error={deleteError}
        onCancel={() => {
          setDeleteConfirm(false);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
      >
        <p className="muted" style={{ margin: 0 }}>
          Permanently delete{" "}
          <strong>{rec.title || formatUnixInTimeZone(rec.created_at, displayTz)}</strong>? The video
          and transcript will be removed. This cannot be undone.
        </p>
      </ConfirmModal>

      <div className="entry-grid" style={{ marginTop: "1rem" }}>
        <div>
          {streamUrl ? (
            <video
              ref={videoRef}
              src={streamUrl}
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="card muted">
              {rec.status === "live"
                ? "This session has no saved video yet. Finish a log on the Record page (End session & save) and wait until status is ready."
                : rec.status === "processing_transcribe"
                  ? "Video is being saved and transcribed — refresh in a moment."
                  : "No playable media for this entry yet."}
            </div>
          )}
        </div>
        <div>
          <h2>Transcript</h2>
          {rec.transcript ? (
            segments.length > 0 ? (
              segments.map((s, i) => (
                <div
                  key={i}
                  className={`transcript-line ${activeLine === i ? "active" : ""}`}
                  onClick={() => seekTo(s.start, i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") seekTo(s.start, i);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {s.text}
                </div>
              ))
            ) : (
              <div className="card" style={{ whiteSpace: "pre-wrap" }}>
                {rec.transcript}
              </div>
            )
          ) : (
            <p className="muted">
              {rec.status === "processing_transcribe"
                ? "Transcription in progress…"
                : "No transcript yet."}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
